/* eslint-disable @typescript-eslint/no-unused-vars */
import { inject, Injectable } from '@angular/core';
import {
  ZIM,
  ZIMBlacklistChangedEventResult,
  ZIMCallAcceptanceSentResult,
  ZIMCallAcceptConfig,
  ZIMCallCancelConfig,
  ZIMCallCancelSentResult,
  ZIMCallEndConfig,
  ZIMCallEndSentResult,
  ZIMCallingInvitationSentResult,
  ZIMCallingInviteConfig,
  ZIMCallInvitationCancelledEventResult,
  ZIMCallInvitationCreatedEventResult,
  ZIMCallInvitationEndedEventResult,
  ZIMCallInvitationListQueriedResult,
  ZIMCallInvitationQueryConfig,
  ZIMCallInvitationReceivedEventResult,
  ZIMCallInvitationSentResult,
  ZIMCallInvitationTimeoutEventResult,
  ZIMCallInviteConfig,
  ZIMCallJoinConfig,
  ZIMCallJoinSentResult,
  ZIMCallQuitConfig,
  ZIMCallQuitSentResult,
  ZIMCallRejectConfig,
  ZIMCallRejectionSentResult,
  ZIMCallUserStateChangedEventResult,
} from 'zego-zim-web';

import { ZimService } from '../../../services/zim.service';

/**
 * Service for handling ZIM call-related operations in the call feature.
 * This service provides methods to manage call invitations, acceptances, rejections, and event listeners.
 * Note: Always use unlisten methods to remove event listeners when no longer needed to prevent memory leaks.
 * Requires ZIM to be initialized and user logged in via ZimService before use.
 */
@Injectable({
  providedIn: 'root',
})
export class CallZimService {
  private zim: ZIM | null = null;
  private zimService = inject(ZimService);

  constructor() {
    this.zim = this.zimService.getZIM();
  }

  /**
   * Checks if ZIM is ready for call operations (initialized and logged in).
   * @returns True if ready, false otherwise.
   */
  isReady(): boolean {
    return this.zim !== null && this.zimService.isLoginZim();
  }

  /**
   * Ensures that the ZIM instance is initialized and the user is logged in.
   * @private
   * @returns The ZIM SDK instance.
   * @throws Error if ZIM is not initialized or user is not logged in.
   */
  private ensureZim(): ZIM {
    if (!this.zim) {
      throw new Error(
        'ZIM instance is not initialized. Please call ZimService.createZIM() and login first.',
      );
    }
    if (!this.zimService.isLoginZim) {
      throw new Error('User is not logged in to ZIM. Please login before using call functions.');
    }
    return this.zim;
  }

  /**
   * Sends a call invitation to the specified invitees.
   * @param invitees Array of user IDs to invite.
   * @param config Configuration for the call invitation.
   * @returns Promise resolving to the result of sending the call invitation.
   */
  async callInvite(
    invitees: string[],
    config: ZIMCallInviteConfig,
  ): Promise<ZIMCallInvitationSentResult | null> {
    try {
      return await this.ensureZim().callInvite(invitees, config);
    } catch (error) {
      console.log('DEBUG-CALL', error);
      return null;
    }
  }

  /**
   * Joins an existing call.
   * @param callID The ID of the call to join.
   * @param config Configuration for joining the call.
   * @returns Promise resolving to the result of joining the call.
   */
  async callJoin(callID: string, config: ZIMCallJoinConfig): Promise<ZIMCallJoinSentResult | null> {
    try {
      return await this.ensureZim().callJoin(callID, config);
    } catch {
      return null;
    }
  }

  /**
   * Cancels a call invitation.
   * @param invitees Array of user IDs whose invitations are being canceled.
   * @param callID The ID of the call.
   * @param config Configuration for canceling the call.
   * @returns Promise resolving to the result of canceling the call.
   */
  async callCancel(
    invitees: string[],
    callID: string,
    config: ZIMCallCancelConfig,
  ): Promise<ZIMCallCancelSentResult | null> {
    try {
      return await this.ensureZim().callCancel(invitees, callID, config);
    } catch {
      return null;
    }
  }

  /**
   * Accepts a call invitation.
   * @param callID The ID of the call to accept.
   * @param config Configuration for accepting the call.
   * @returns Promise resolving to the result of accepting the call.
   */
  async callAccept(
    callID: string,
    config: ZIMCallAcceptConfig,
  ): Promise<ZIMCallAcceptanceSentResult | null> {
    try {
      return await this.ensureZim().callAccept(callID, config);
    } catch {
      return null;
    }
  }

  /**
   * Rejects a call invitation.
   * @param callID The ID of the call to reject.
   * @param config Configuration for rejecting the call.
   * @returns Promise resolving to the result of rejecting the call.
   */
  async callReject(
    callID: string,
    config: ZIMCallRejectConfig,
  ): Promise<ZIMCallRejectionSentResult | null> {
    try {
      return await this.ensureZim().callReject(callID, config);
    } catch (err) {
      return null;
    }
  }

  /**
   * Quits an ongoing call.
   * @param callID The ID of the call to quit.
   * @param config Configuration for quitting the call.
   * @returns Promise resolving to the result of quitting the call.
   */
  async callQuit(callID: string, config: ZIMCallQuitConfig): Promise<ZIMCallQuitSentResult | null> {
    try {
      return await this.ensureZim().callQuit(callID, config);
    } catch (err) {
      return null;
    }
  }

  /**
   * Ends a call.
   * @param callID The ID of the call to end.
   * @param config Configuration for ending the call.
   * @returns Promise resolving to the result of ending the call.
   */
  async callEnd(callID: string, config: ZIMCallEndConfig): Promise<ZIMCallEndSentResult | null> {
    try {
      return await this.ensureZim().callEnd(callID, config);
    } catch (err) {
      return null;
    }
  }

  /**
   * Sends an invitation to additional users during an ongoing call.
   * @param invitees Array of user IDs to invite.
   * @param callID The ID of the ongoing call.
   * @param config Configuration for the calling invitation.
   * @returns Promise resolving to the result of sending the calling invitation.
   */
  async callingInvite(
    invitees: string[],
    callID: string,
    config: ZIMCallingInviteConfig,
  ): Promise<ZIMCallingInvitationSentResult | null> {
    try {
      return await this.ensureZim().callingInvite(invitees, callID, config);
    } catch {
      return null;
    }
  }

  /**
   * Queries the list of call invitations.
   * @param config Configuration for querying call invitations.
   * @returns Promise resolving to the result of querying call invitations.
   */
  async queryCallInvitationList(
    config: ZIMCallInvitationQueryConfig,
  ): Promise<ZIMCallInvitationListQueriedResult> {
    return await this.ensureZim().queryCallInvitationList(config);
  }

  /**
   * Listens for blacklist changes.
   * @param callback Callback function to handle the event.
   */
  listenBlacklistChanged(callback: (zim: ZIM, data: ZIMBlacklistChangedEventResult) => void): void {
    this.ensureZim().on('blacklistChanged', callback);
  }

  /**
   * Stops listening for blacklist changes.
   */
  unlistenBlacklistChanged(): void {
    this.ensureZim().off('blacklistChanged');
  }

  /**
   * Listens for received call invitations.
   * @param callback Callback function to handle the event.
   */
  listenCallInvitationReceived(
    callback: (zim: ZIM, data: ZIMCallInvitationReceivedEventResult) => void,
  ): void {
    this.ensureZim().on('callInvitationReceived', callback);
  }

  /**
   * Stops listening for received call invitations.
   */
  unlistenCallInvitationReceived(): void {
    this.ensureZim().off('callInvitationReceived');
  }

  /**
   * Listens for created call invitations.
   * @param callback Callback function to handle the event.
   */
  listenCallInvitationCreated(
    callback: (zim: ZIM, data: ZIMCallInvitationCreatedEventResult) => void,
  ): void {
    this.ensureZim().on('callInvitationCreated', callback);
  }

  /**
   * Stops listening for created call invitations.
   */
  unlistenCallInvitationCreated(): void {
    this.ensureZim().off('callInvitationCreated');
  }

  /**
   * Listens for canceled call invitations.
   * @param callback Callback function to handle the event.
   */
  listenCallInvitationCancelled(
    callback: (zim: ZIM, data: ZIMCallInvitationCancelledEventResult) => void,
  ): void {
    this.ensureZim().on('callInvitationCancelled', callback);
  }

  /**
   * Stops listening for canceled call invitations.
   */
  unlistenCallInvitationCancelled(): void {
    this.ensureZim().off('callInvitationCancelled');
  }

  /**
   * Listens for call invitation timeouts.
   * @param callback Callback function to handle the event.
   */
  listenCallInvitationTimeout(
    callback: (zim: ZIM, data: ZIMCallInvitationTimeoutEventResult) => void,
  ): void {
    this.ensureZim().on('callInvitationTimeout', callback);
  }

  /**
   * Stops listening for call invitation timeouts.
   */
  unlistenCallInvitationTimeout(): void {
    this.ensureZim().off('callInvitationTimeout');
  }

  /**
   * Listens for ended call invitations.
   * @param callback Callback function to handle the event.
   */
  listenCallInvitationEnded(
    callback: (zim: ZIM, data: ZIMCallInvitationEndedEventResult) => void,
  ): void {
    this.ensureZim().on('callInvitationEnded', callback);
  }

  /**
   * Stops listening for ended call invitations.
   */
  unlistenCallInvitationEnded(): void {
    this.ensureZim().off('callInvitationEnded');
  }

  /**
   * Listens for changes in call user states.
   * @param callback Callback function to handle the event.
   */
  listenCallUserStateChanged(
    callback: (zim: ZIM, data: ZIMCallUserStateChangedEventResult) => void,
  ): void {
    this.ensureZim().on('callUserStateChanged', callback);
  }

  /**
   * Stops listening for changes in call user states.
   */
  unlistenCallUserStateChanged(): void {
    this.ensureZim().off('callUserStateChanged');
  }

  uploadLogZim(): void {
    this.ensureZim().uploadLog();
  }
}
